import { supabase } from '../../config/supabase';
import { 
  Voucher, 
  VoucherCode, 
  CreateVoucherRequest, 
  UpdateVoucherRequest, 
  VoucherFilters,
  VoucherStats,
  VoucherWithCodes
} from '../../types/voucher.types';

export interface VoucherListResponse {
  data: VoucherWithCodes[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface VoucherCodeListResponse {
  data: VoucherCode[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class VoucherManagementService {
  /**
   * Lấy danh sách voucher với phân trang và filter
   */
  static async getVouchers(
    page: number = 1,
    limit: number = 20,
    filters: VoucherFilters = {}
  ): Promise<VoucherListResponse> {
    try {
      const offset = (page - 1) * limit;
      
      let query = supabase
        .from('voucher')
        .select(`
          *,
          voucher_codes(count)
        `, { count: 'exact' });

      // Apply filters
      if (filters.search) {
        query = query.or(`code.ilike.%${filters.search}%`);
      }

      if (filters.voucher_type) {
        if (filters.voucher_type === 'public') {
          query = query.eq('is_public', true).eq('multi_code', false);
        } else if (filters.voucher_type === 'private') {
          query = query.eq('is_public', false).eq('multi_code', false);
        } else if (filters.voucher_type === 'reward') {
          query = query.eq('is_public', false).eq('multi_code', false);
        } else if (filters.voucher_type === 'random_code') {
          query = query.eq('multi_code', true);
        }
      }

      if (filters.voucher_type_vehicle) {
        query = query.eq('voucher_type_vehicle', filters.voucher_type_vehicle);
      }

      if (filters.status) {
        const now = new Date().toISOString();
        if (filters.status === 'active') {
          query = query.lte('start_date', now).gte('end_date', now);
        } else if (filters.status === 'expired') {
          query = query.lt('end_date', now);
        } else if (filters.status === 'upcoming') {
          query = query.gt('start_date', now);
        }
      }

      if (filters.is_point_voucher !== undefined) {
        if (filters.is_point_voucher) {
          // For point vouchers, ensure point_cost is not null AND > 0
          query = query.not('point_cost', 'is', null).gt('point_cost', 0);
        } else {
          query = query.is('point_cost', null);
        }
      }

      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(error.message);
      }

      const vouchers: VoucherWithCodes[] = (data || []).map(voucher => ({
        ...voucher,
        codes_count: voucher.voucher_codes?.[0]?.count || 0
      }));

      const totalPages = Math.ceil((count || 0) / limit);

      return {
        data: vouchers,
        total: count || 0,
        page,
        limit,
        totalPages
      };
    } catch (error) {
      console.error('Error getting vouchers:', error);
      throw new Error('Không thể lấy danh sách voucher');
    }
  }

  /**
   * Lấy voucher theo ID với codes
   */
  static async getVoucherById(id: string): Promise<VoucherWithCodes> {
    try {
      const { data, error } = await supabase
        .from('voucher')
        .select(`
          *,
          voucher_codes(*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('Không tìm thấy voucher');
      }

      return {
        ...data,
        codes: data.voucher_codes || [],
        codes_count: data.voucher_codes?.length || 0
      };
    } catch (error) {
      console.error('Error getting voucher by ID:', error);
      throw new Error('Không thể lấy thông tin voucher');
    }
  }

  /**
   * Tạo voucher mới
   */
  static async createVoucher(data: CreateVoucherRequest): Promise<VoucherWithCodes> {
    try {
      // Prepare voucher data based on type
      const voucherData = this.prepareVoucherData(data);
      
      console.log('Creating voucher with data:', voucherData);

      const { data: voucher, error } = await supabase
        .from('voucher')
        .insert([voucherData])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(error.message);
      }

      // Generate random codes if needed
      if (data.voucher_type === 'random_code' && data.number_of_codes) {
        await this.generateRandomCodes(voucher.id, data.number_of_codes, data.code_prefix);
      }

      return await this.getVoucherById(voucher.id);
    } catch (error) {
      console.error('Error creating voucher:', error);
      throw new Error('Không thể tạo voucher');
    }
  }

  /**
   * Cập nhật voucher
   */
  static async updateVoucher(data: UpdateVoucherRequest): Promise<VoucherWithCodes> {
    try {
      const { id, ...updateData } = data;
      const voucherData = this.prepareVoucherData(updateData);

      const { error } = await supabase
        .from('voucher')
        .update({
          ...voucherData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }

      return await this.getVoucherById(id);
    } catch (error) {
      console.error('Error updating voucher:', error);
      throw new Error('Không thể cập nhật voucher');
    }
  }

  /**
   * Xóa voucher
   */
  static async deleteVoucher(id: string): Promise<void> {
    try {

      // Then delete the voucher
      const { error } = await supabase
        .from('voucher')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error deleting voucher:', error);
      throw new Error('Không thể xóa voucher');
    }
  }

  /**
   * Lấy thống kê voucher
   */
  static async getVoucherStats(): Promise<VoucherStats> {
    try {
      const now = new Date().toISOString();

      const [totalResult, activeResult, expiredResult, redemptionsResult] = await Promise.all([
        supabase.from('voucher').select('*', { count: 'exact', head: true }),
        supabase.from('voucher').select('*', { count: 'exact', head: true })
          .lte('start_date', now).gte('end_date', now),
        supabase.from('voucher').select('*', { count: 'exact', head: true })
          .lt('end_date', now),
        supabase.from('voucher').select('total_redemptions')
      ]);

      const totalRedemptions = redemptionsResult.data?.reduce((sum, v) => sum + (v.total_redemptions || 0), 0) || 0;

      return {
        total_vouchers: totalResult.count || 0,
        active_vouchers: activeResult.count || 0,
        expired_vouchers: expiredResult.count || 0,
        total_redemptions: totalRedemptions
      };
    } catch (error) {
      console.error('Error getting voucher stats:', error);
      throw new Error('Không thể lấy thống kê voucher');
    }
  }

  /**
   * Tạo mã ngẫu nhiên cho voucher
   */
  static async generateRandomCodes(
    voucherId: string, 
    numberOfCodes: number, 
    prefix: string = 'VOUCHER'
  ): Promise<VoucherCode[]> {
    try {
      const codes: Omit<VoucherCode, 'id' | 'created_at' | 'updated_at'>[] = [];
      
      for (let i = 0; i < numberOfCodes; i++) {
        const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
        const code = `${prefix}${randomSuffix}`;
        
        codes.push({
          voucher_id: voucherId,
          code,
          is_redeemed: false,
          redeemed_at: undefined,
          redeemed_by: undefined
        });
      }

      const { data, error } = await supabase
        .from('voucher_codes')
        .insert(codes)
        .select();

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error generating random codes:', error);
      throw new Error('Không thể tạo mã ngẫu nhiên');
    }
  }

  /**
   * Lấy danh sách mã voucher
   */
  static async getVoucherCodes(
    voucherId: string,
    page: number = 1,
    limit: number = 50,
    filters: {
      search?: string;
      is_redeemed?: boolean;
    } = {}
  ): Promise<VoucherCodeListResponse> {
    try {
      const offset = (page - 1) * limit;
      
      let query = supabase
        .from('voucher_codes')
        .select('*', { count: 'exact' })
        .eq('voucher_id', voucherId);

      // Apply filters
      if (filters.search) {
        query = query.ilike('code', `%${filters.search}%`);
      }

      if (filters.is_redeemed !== undefined) {
        query = query.eq('is_redeemed', filters.is_redeemed);
      }

      // Apply pagination and ordering
      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('Supabase error getting voucher codes:', error);
        throw new Error(error.message);
      }

      const codes: VoucherCode[] = data?.map((code: any) => ({
        id: code.id,
        voucher_id: code.voucher_id,
        code: code.code,
        is_redeemed: code.is_redeemed,
        redeemed_at: code.redeemed_at,
        redeemed_by: code.redeemed_by,
        redeemed_by_user: code.users ? {
          id: code.users.id,
          full_name: code.users.full_name,
          phone_number: code.users.phone_number
        } : undefined,
        created_at: code.created_at,
        updated_at: code.updated_at
      })) || [];

      const totalPages = Math.ceil((count || 0) / limit);

      return {
        data: codes,
        total: count || 0,
        page,
        limit,
        totalPages
      };
    } catch (error) {
      console.error('Error getting voucher codes:', error);
      throw new Error('Không thể lấy danh sách mã code');
    }
  }

  /**
   * Xóa mã voucher
   */
  static async deleteVoucherCode(voucherId: string, codeId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('voucher_codes')
        .delete()
        .eq('id', codeId)
        .eq('voucher_id', voucherId);

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error deleting voucher code:', error);
      throw new Error('Không thể xóa mã voucher');
    }
  }

  /**
   * Validate voucher code
   */
  static async validateVoucherCode(code: string): Promise<{
    valid: boolean;
    voucher?: Voucher;
    message: string;
  }> {
    try {
      // Check in voucher table first (for private vouchers)
      let { data: voucher } = await supabase
        .from('voucher')
        .select('*')
        .eq('code', code)
        .single();

      // If not found, check in voucher_codes table (for random code vouchers)
      if (!voucher) {
        const { data: voucherCode } = await supabase
          .from('voucher_codes')
          .select(`
            *,
            voucher:voucher_id(*)
          `)
          .eq('code', code)
          .single();

        if (voucherCode && !voucherCode.is_redeemed) {
          voucher = voucherCode.voucher;
        }
      }

      if (!voucher) {
        return {
          valid: false,
          message: 'Mã voucher không tồn tại'
        };
      }

      const now = new Date();
      const startDate = new Date(voucher.start_date);
      const endDate = new Date(voucher.end_date);

      if (now < startDate) {
        return {
          valid: false,
          voucher,
          message: 'Voucher chưa có hiệu lực'
        };
      }

      if (now > endDate) {
        return {
          valid: false,
          voucher,
          message: 'Voucher đã hết hạn'
        };
      }

      return {
        valid: true,
        voucher,
        message: 'Voucher hợp lệ'
      };
    } catch (error) {
      console.error('Error validating voucher code:', error);
      return {
        valid: false,
        message: 'Không thể kiểm tra mã voucher'
      };
    }
  }

  /**
   * Clone voucher
   */
  static async cloneVoucher(id: string): Promise<VoucherWithCodes> {
    try {
      const originalVoucher = await this.getVoucherById(id);
      
      const clonedData: CreateVoucherRequest = {
        title: `${originalVoucher.title} (Copy)`,
        description: originalVoucher.description,
        voucher_type: originalVoucher.multi_code ? 'random_code' : 
                    originalVoucher.is_public ? 'public' : 
                    (originalVoucher.point_cost && originalVoucher.point_cost > 0) ? 'reward' : 'private',
        discount_value: originalVoucher.discount_value,
        discount_percentage: originalVoucher.discount_percentage,
        max_discount: originalVoucher.max_discount,
        start_date: originalVoucher.start_date,
        end_date: originalVoucher.end_date,
        max_usage: originalVoucher.max_usage,
        minimum_amount: originalVoucher.minimum_amount,
        voucher_type_vehicle: originalVoucher.voucher_type_vehicle,
        code: originalVoucher.code ? `${originalVoucher.code}_COPY` : undefined,
        point_cost: originalVoucher.point_cost,
        total_redemption_limit: originalVoucher.total_redemption_limit,
        user_redemption_limit: originalVoucher.user_redemption_limit
      };

      return await this.createVoucher(clonedData);
    } catch (error) {
      console.error('Error cloning voucher:', error);
      throw new Error('Không thể sao chép voucher');
    }
  }

  /**
   * Prepare voucher data based on type
   */
  private static prepareVoucherData(data: CreateVoucherRequest | Partial<CreateVoucherRequest>) {
    console.log('prepareVoucherData - Input data:', data);
    
    const baseData = {
      title: data.title || '',
      description: data.description || '',
      discount_value: data.discount_value || null,
      discount_percentage: data.discount_percentage || null,
      max_discount: data.max_discount || null,
      start_date: data.start_date,
      end_date: data.end_date,
      max_usage: data.max_usage || 1,
      minimum_amount: data.minimum_amount || null,
      voucher_type_vehicle: data.voucher_type_vehicle || null,
      point_cost: data.point_cost || null,
      total_redemption_limit: data.total_redemption_limit || null,
      user_redemption_limit: data.user_redemption_limit || null,
      total_redemptions: 0
    };

    console.log('prepareVoucherData - Base data:', baseData);
    console.log('prepareVoucherData - Voucher type:', data.voucher_type);

    switch (data.voucher_type) {
      case 'public':
        const publicData = {
          ...baseData,
          is_public: true,
          multi_code: false,
          code: null
        };
        console.log('prepareVoucherData - Public voucher data:', publicData);
        return publicData;
        
      case 'private':
        const privateData = {
          ...baseData,
          is_public: false,
          multi_code: false,
          code: data.code || null
        };
        console.log('prepareVoucherData - Private voucher data:', privateData);
        return privateData;
        
      case 'reward':
        const rewardData = {
          ...baseData,
          is_public: false,
          multi_code: false,
          code: data.code || null
        };
        console.log('prepareVoucherData - Reward voucher data:', rewardData);
        return rewardData;
        
      case 'random_code':
        const randomCodeData = {
          ...baseData,
          is_public: false,
          multi_code: true,
          code: null
        };
        console.log('prepareVoucherData - Random code voucher data:', randomCodeData);
        return randomCodeData;
        
      default:
        throw new Error('Invalid voucher type');
    }
  }
}
